import {
    AddCommentRequest,
    type AddCommentResponse,
    type CommentItem,
    UpdateCommentRequest,
    type UpdateCommentResponse,
} from '@app/_proto/Protos/comments';
import { CommentsClient } from '@app/_proto/Protos/comments.client';
import { Int32Value, StringValue } from '@app/_proto/google/protobuf/wrappers';
import ButtonSpinner from '@app/components/button.spinner';
import ChapterCommentPolicy from '@app/components/chapters/chapter.comment.policy';
import { useComponentClasses } from '@app/components/hooks';
import { useCurrentChapter } from '@app/domains/chapter/hooks/useCurrentChapter';
import { GrpcError, type GrpcMessage } from '@app/libs/grpc';
import { useHttp } from '@app/libs/http';
import { batch } from '@app/libs/utils';
import { breakpoints } from '@app/utils/breakpoints';
import { Button, useMediaQuery } from '@mui/material';
import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

//#region : Main component

/** Custom. Main component parameters type */
export type CommentSubmitProps = {
    entityId?: number;
    parentComment?: CommentItem;
    paragraphId?: string;
    isEdit?: boolean;
    disabled?: boolean;
    disabledMessage?: string;
    onCommentSubmitted?: (id: number) => void;
};

/** Custom. Shows comment textarea and Handles comment actions - new, edit and reply. */
export default function CommentSubmitComponent({
    entityId,
    parentComment,
    paragraphId,
    isEdit,
    disabled: submitDisabled,
    disabledMessage,
    onCommentSubmitted,
}: CommentSubmitProps) {
    //#region : Variables, functions and api calls

    //General
    const [content, setContent] = useState('');
    const [error, setError] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [showCommentPolicy, setShowCommentPolicy] = useState<boolean>(false);

    const isMobile = useMediaQuery(breakpoints.downSm, {
        noSsr: true,
    });

    const chapter = useCurrentChapter();

    const queryClient = useQueryClient();

    const { MuiButton: buttonClasses } = useComponentClasses('MuiButton', 'MuiOutlinedInput');

    //If user is editing his comment, show the previous comment.
    //Else set blank text.
    useEffect(() => {
        setContent(isEdit ? parentComment?.content?.value || '' : '');
    }, [isEdit, parentComment?.content]);

    //#region : Submit Comment
    const { grpcRequest } = useHttp();

    const onClickSubmit = useCallback(async () => {
        setDisabled(true);
        let response: GrpcMessage<AddCommentResponse | UpdateCommentResponse> | null = null;

        try {
            if (!isEdit) {
                const request = AddCommentRequest.create({
                    entityId,
                    content,
                    paragraphId: paragraphId ? StringValue.create({ value: paragraphId }) : undefined,
                    parentId: parentComment
                        ? Int32Value.create({
                              value: parentComment.id,
                          })
                        : undefined,
                });

                response = await grpcRequest(CommentsClient, c => c.addComment, request);

                if (paragraphId) {
                    queryClient.invalidateQueries(['chapter', chapter?.novelInfo?.slug, chapter?.slug]);
                }

                queryClient.invalidateQueries(['comments', entityId, undefined]);
            } else if (parentComment?.id) {
                const request = UpdateCommentRequest.create({
                    id: parentComment.id,
                    content,
                });

                response = await grpcRequest(CommentsClient, c => c.updateComment, request);
            }

            batch(() => {
                setContent('');
                setError('');
                setDisabled(false);
            });

            if (response?.item) {
                onCommentSubmitted?.(response.item.id);
            }
        } catch (e) {
            if (e instanceof GrpcError) {
                const error = e;

                console.error(error.message, error.response);

                batch(() => {
                    if (error?.response) {
                        const errorsList = error.response.errors;

                        if (errorsList.length > 0) {
                            setError(errorsList.join('\n'));
                        } else if (error.status === GrpcStatusCode.PERMISSION_DENIED) {
                            setError('Permission denied');
                        } else {
                            setError(error.response.description || error.message);
                        }
                    } else {
                        setError(error.message);
                    }

                    setDisabled(false);
                });
            } else {
                throw e;
            }
        }
    }, [
        chapter?.novelInfo?.slug,
        chapter?.slug,
        content,
        entityId,
        grpcRequest,
        isEdit,
        onCommentSubmitted,
        paragraphId,
        parentComment,
        queryClient,
    ]);

    const openCommentPolicyDialog = () => {
        setShowCommentPolicy(true);
    };

    const hideCommentPolicyDialog = () => {
        setShowCommentPolicy(false);
    };

    //#endregion : Submit Comment

    //#endregion : Variables, functions and api calls

    return (
        <div className="relative">
            {error?.length > 0 && (
                <div className="mb-[15px] text-[13px] font-semibold leading-tight text-[#dd3545]">
                    {error.split('\n').map((err, idx) => (
                        <p key={idx}>{err}</p>
                    ))}
                </div>
            )}
            {submitDisabled && disabledMessage && (
                <div className="text-[13px] font-semibold leading-tight text-[#dd3545] absolute h-[64px] min-h-[64px] p-[10px] sm:h-[70px] sm:min-h-[70px] top-0 left-0 right-0 bottom-0 text-center flex justify-center items-center">
                    {disabledMessage}
                </div>
            )}
            <textarea
                placeholder={
                    submitDisabled
                        ? ''
                        : parentComment
                          ? `Reply to ${parentComment?.poster?.userName}`
                          : 'Add a comment'
                }
                className="h-[64px] min-h-[64px] w-full rounded-md border-2 border-transparent bg-black/[.04] p-[10px] hover:shadow-md focus:!border-blue dark:bg-[#242424] sm:h-[70px] sm:min-h-[70px]"
                disabled={disabled || submitDisabled}
                value={content}
                rows={isMobile ? 3 : 5}
                onChange={e => setContent(e.target.value)}
                aria-label="comment"
            ></textarea>
            <div className="justify-stretch flex flex-col justify-end">
                <div>
                    <ButtonSpinner
                        className="mt-[12px]"
                        classes={{
                            ...buttonClasses,
                            root: twMerge(buttonClasses.root, 'w-auto'),
                            sizeMedium: twMerge(buttonClasses.sizeMedium, '!text-[13px]'),
                        }}
                        color="primary"
                        onClick={onClickSubmit}
                        disabled={disabled || submitDisabled}
                        spinner={disabled}
                    >
                        {!isEdit ? <span>Submit</span> : <span>Edit</span>}
                    </ButtonSpinner>
                </div>
                <div className="!text-[9px] mt-15">
                    By submitting this comment I confirm I have read and accepted the{' '}
                    <Button
                        onClick={openCommentPolicyDialog}
                        variant="outlined"
                        color="secondary"
                        className="!text-[9px] text-inherit font-normal underline p-0 border-none hover:border-none hover:bg-transparent hover:shadow-none hover:text-blue"
                    >
                        comment policy
                    </Button>
                    .
                </div>
            </div>
            <ChapterCommentPolicy onClose={hideCommentPolicyDialog} open={showCommentPolicy} />
        </div>
    );
}
//#endregion : Main component
